import { z } from 'zod';

const unknownRecordSchema = z.record(z.string(), z.unknown());
const stringOrNumberSchema = z.union([z.string(), z.number()]);

export const embedPaymentSessionWireSchema = z.object({
  kind: z.enum(['address', 'redirect', 'embed_pending']),
  gateway: z.string(),
  checkout_url: z.string().nullable().optional(),
  address: z.string().optional(),
  amount: z.string().optional(),
  qr_code: z.string().optional(),
  expires_at: z.string().optional(),
  payment_id: z.string().optional(),
  confirmations_needed: z.number().optional(),
}).passthrough();

const publicInvoiceWireCustomFieldDefinitionSchema = z.object({
  collect_after_payment: z.boolean().optional(),
  default_value: z.string().optional(),
  min_length: z.number().optional(),
  name: z.string(),
  placeholder: z.string().optional(),
  regex: z.string().optional(),
  required: z.boolean(),
  type: z.string(),
});

/**
 * Post-payment custom fields the buyer still owes before the deferred line
 * items ship. Produced by `serializePendingPostPaymentCustomFields` /
 * `parsePendingPostPaymentCustomFields` on the backend and surfaced on the
 * public + dashboard invoice wire as `pending_custom_fields` (null once
 * submitted). Exported so the checkout and the dashboard validate the block
 * against the producer's shape instead of restating it.
 */
export const pendingPostPaymentCustomFieldsSchema = z.object({
  version: z.literal(1),
  line_items: z.array(z.object({
    line_item_id: z.string().uuid(),
    product_id: z.string().uuid(),
    product_title: z.string(),
    fields: z.array(publicInvoiceWireCustomFieldDefinitionSchema.extend({
      collect_after_payment: z.literal(true),
    })),
  })),
});

export type PendingPostPaymentCustomFieldsWire = z.infer<typeof pendingPostPaymentCustomFieldsSchema>;
export type PendingPostPaymentCustomFieldsLineItemWire = PendingPostPaymentCustomFieldsWire['line_items'][number];
export type PendingPostPaymentCustomFieldWire = PendingPostPaymentCustomFieldsLineItemWire['fields'][number];

const publicInvoiceWireProductAddonSchema = z.object({
  id: z.string(),
  price: z.number(),
  quantity: z.number(),
  title: z.string(),
});

const publicInvoiceWireAvailableAddonSchema = z.object({
  currency: z.string(),
  description: z.string(),
  id: z.string(),
  price: z.number(),
  title: z.string(),
  uniqid: z.string(),
});

/**
 * A null display price is only a legal wire state for PWYW products; a
 * fixed-price producer omitting it is a broken contract and must keep
 * failing fast (AGENTS.md forward-only rule).
 */
function requirePriceDisplayUnlessPayWhatYouWant(
  product: { price_display: string | number | null; pay_what_you_want?: boolean },
  ctx: z.RefinementCtx,
): void {
  if (product.price_display === null && product.pay_what_you_want !== true) {
    ctx.addIssue({
      code: 'custom',
      path: ['price_display'],
      message: 'price_display may only be null for pay_what_you_want products.',
    });
  }
}

const publicInvoiceWireProductSchema = z.object({
  addons: z.array(publicInvoiceWireProductAddonSchema).optional(),
  /** Per-line cart-edit capability from the enricher; the checkout renders no
   *  control without an explicit true. */
  quantity_editable: z.boolean().optional(),
  addons_editable: z.boolean().optional(),
  removable: z.boolean().optional(),
  available_addons: z.array(z.unknown()).optional(),
  cloudflare_image_id: z.string().nullable(),
  created_at: z.string().optional(),
  currency: z.string(),
  custom_fields: unknownRecordSchema.nullable().optional(),
  custom_fields_config: z.array(publicInvoiceWireCustomFieldDefinitionSchema).nullable().optional(),
  post_payment_custom_fields_config: z.array(publicInvoiceWireCustomFieldDefinitionSchema).nullable().optional(),
  delivery_instruction: z.unknown().optional(),
  delivery_instruction_config: z.unknown().optional(),
  delivery_instruction_label: z.unknown().optional(),
  delivery_text: z.string().nullable().optional(),
  // Delivery wait as a bare COUNT. `0` and `null` both mean instant (the
  // digital-goods default); a positive value is a "within N <unit>" promise.
  delivery_time: z.number().nullable().optional(),
  // The unit `delivery_time` is counted in. The column is NOT NULL with a
  // `days` default — which is what every row meant before the unit existed — so
  // a live producer always sends one. It stays optional here because this
  // schema also describes payment-link and other synthesized products that
  // carry no catalog row, and those quote no wait at all.
  delivery_time_unit: z.enum(['minutes', 'hours', 'days']).nullable().optional(),
  description: z.string().nullable(),
  discount_percent: z.number().optional(),
  discount_display: z.number().optional(),
  discounted_total_display: z.number().optional(),
  gateways: z.array(z.string()).nullable().optional(),
  id: z.string().optional(),
  image_name: z.string().nullable(),
  image_storage: z.string().nullable().optional(),
  line_item_id: z.string().nullable().optional(),
  pay_what_you_want: z.boolean().optional(),
  price: z.string().optional(),
  // Nullable ONLY for PWYW products (no fixed catalog price until the buyer
  // chooses one) — enforced by requirePriceDisplayUnlessPayWhatYouWant below,
  // so a fixed-price producer dropping the field still fails fast.
  price_display: stringOrNumberSchema.nullable(),
  quantity: z.number(),
  quantity_max: z.number().optional(),
  quantity_min: z.number().optional(),
  recurring_interval: z.string().nullable().optional(),
  recurring_interval_count: z.number().nullable().optional(),
  redirect_link: z.string().nullable().optional(),
  redirect_time: z.number().nullable().optional(),
  service_text: z.string().nullable().optional(),
  setup_cost: z.string().optional(),
  slug: z.string().nullable().optional(),
  storefront_url: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  subtype: z.string().nullable().optional(),
  terms_of_service: z.string().nullable().optional(),
  title: z.string(),
  total: z.number(),
  trial_period: z.number().nullable().optional(),
  type: z.string().nullable().optional(),
  uniqid: z.string().nullable(),
  unit_price: z.number().optional(),
  unit_price_display: stringOrNumberSchema.optional(),
  unit_quantity: z.number(),
  updated_at: z.string().optional(),
  variant_title: z.string().nullable().optional(),
  video_link: z.string().nullable().optional(),
  volume_discounts: z.unknown().optional(),
  /** The SHORT replacement/refund eligibility window, in SECONDS. */
  warranty_days: z.number().nullable().optional(),
  warranty_text: z.string().nullable().optional(),
  /**
   * Long-term per-purchase coverage in DAYS, or null/absent for none.
   *
   * Deliberately a second field beside `warranty_days` rather than a rename:
   * they are different features that a similar name keeps conflating.
   * `warranty_days` is the seconds-long refund window; this is
   * Before payment this is the current `products.warranty_period_days` value.
   * After completion it is the invoice line's activated
   * `order_item_warranties.period_days` snapshot, so later product edits cannot
   * rewrite the buyer's receipt. Buyer surfaces read THIS one — reading
   * `warranty_days` would print a seconds count as a day count.
   */
  warranty_period_days: z.number().nullable().optional(),
});

const gatewayFeePreviewSchema = z.object({
  amount: z.number(),
  gateway: z.string(),
});

const checkoutTippingSchema = z.object({
  custom_tip_enabled: z.boolean().optional(),
  enabled: z.boolean(),
  preset_percentages: z.array(z.number()).optional(),
});

const customerBalanceSchema = z.object({
  allow_partial_payment: z.boolean().optional(),
  auto_apply: z.boolean().optional(),
  available: z.string(),
  currency: z.string(),
});

const pricingBreakdownSchema = z.object({
  affiliate_code: z.string().nullable().optional(),
  already_paid: stringOrNumberSchema.nullable().optional(),
  amount_due: stringOrNumberSchema.nullable().optional(),
  currency: z.string().nullable().optional(),
  discount: stringOrNumberSchema.nullable().optional(),
  volume_discount: stringOrNumberSchema.nullable().optional(),
  discount_label: z.string().nullable().optional(),
  gateway_fee: stringOrNumberSchema.nullable().optional(),
  platform_fee: stringOrNumberSchema.nullable().optional(),
  processing_fee: stringOrNumberSchema.nullable().optional(),
  subtotal: stringOrNumberSchema.nullable().optional(),
  tip: stringOrNumberSchema.nullable().optional(),
  total: stringOrNumberSchema.nullable().optional(),
});

const pollingSchema = z.object({
  active: z.boolean(),
  expires_at: z.string(),
});

const supportSchema = z.object({
  faq_url: z.string().nullable(),
  support_email: z.string().nullable(),
  support_url: z.string().nullable(),
  telegram_invite: z.string().nullable(),
  /** shop_social_links.discord — the checkout renders an optional join row from it. */
  discord_invite_url: z.string().nullable(),
});

const gatewayEligibilityStateSchema = z.object({
  currency: z.string().nullable().optional(),
  enabled: z.boolean(),
  max_amount: z.number().nullable().optional(),
  min_amount: z.number().nullable().optional(),
  reason: z.enum(['BELOW_MIN_AMOUNT', 'ABOVE_MAX_AMOUNT']).nullable().optional(),
});

const deliverySchema = z.object({
  delivered_items: z.array(z.unknown()),
  downloads: z.array(z.unknown()),
  external_urls: z.array(z.unknown()),
  license_keys: z.array(z.unknown()),
  tracking_codes: z.array(z.unknown()),
});

const underpaymentSchema = z.object({
  active: z.boolean().optional(),
  attempt_id: z.string().optional(),
  buyer_actionable: z.boolean().nullable().optional(),
  crypto_currency: z.string().nullable().optional(),
  currency: z.string().optional(),
  expected_amount: z.string().nullable().optional(),
  expected_crypto_amount: z.string().nullable().optional(),
  last_checked_at: z.string().optional(),
  missing_amount: z.string().nullable().optional(),
  missing_crypto_amount: z.string().nullable().optional(),
  // InvoiceEnricher.applyUnderpayment serializes providerReference ?? null —
  // an active underpayment without a provider reference is a valid payload.
  provider_reference: z.string().nullable().optional(),
  provider_status: z.string().nullable().optional(),
  received_amount: z.string().nullable().optional(),
  /** Display-only fiat estimate for the crypto amount observed on-chain. */
  received_fiat_estimate: z.string().nullable().optional(),
  received_crypto_amount: z.string().nullable().optional(),
  shortfall_percent: z.string().nullable().optional(),
  buyer_action_reason: z.enum([
    'BELOW_MINIMUM_COLLECTIBLE',
    'PROVIDER_SESSION_CLOSED',
    'PROVIDER_TOP_UP_UNSUPPORTED',
    'MERCHANT_REVIEW_REQUIRED',
  ]).nullable().optional(),
});

const statusHistoryEntrySchema = z.object({
  created_at: z.string().nullable(),
  details: z.string().nullable(),
  id: z.string(),
  invoice_id: z.string(),
  status: z.string().nullable(),
});

const paymentMethodOverrideSchema = z.object({
  billing_address_mode: z.enum(['INHERIT', 'REQUIRED', 'DISABLED']).optional(),
  button_label: z.string().nullable().optional(),
  display_name: z.string().nullable().optional(),
  gateway: z.string(),
  hide_provider_attribution: z.boolean().optional(),
  icon_url: z.string().nullable().optional(),
  terms_mode: z.enum(['INHERIT', 'REQUIRED', 'DISABLED']).optional(),
});

const customGatewaySchema = z.object({
  custom_fields: z.array(z.unknown()).optional(),
  description: z.string().nullable(),
  display_order: z.number(),
  icon_preset: z.string().nullable(),
  icon_url: z.string().nullable(),
  id: z.string(),
  instructions: z.string().nullable(),
  is_active: z.boolean().optional(),
  name: z.string(),
  payment_type: z.enum(['INSTRUCTIONS', 'REDIRECT']),
  redirect_url: z.string().nullable(),
  require_proof: z.boolean(),
  // Which fields the buyer has to fill in when proof is required. Optional so
  // an older cached payload stays parseable; the checkout falls back to BOTH.
  proof_type: z.enum(['TEXT', 'IMAGE', 'BOTH']).optional(),
  void_after_hours: z.number(),
});

const externalPaymentAdapterSchema = z.object({
  description: z.string().nullable(),
  display_order: z.number(),
  icon_url: z.string().nullable(),
  id: z.string(),
  name: z.string(),
});

const buyerIdentitySchema = z.object({
  email: z.object({
    masked: z.string().nullable(),
    persisted: z.boolean(),
    required_for_provider_session: z.boolean().optional(),
  }),
});

const cryptoTransactionSchema = z.object({
  amount: z.string().nullable(),
  confirmations: z.number().nullable(),
  created_at: z.number().optional(),
  hash: z.string().nullable(),
  status: z.string().optional(),
});

const voidTimeSchema = z.object({
  conf: z.object({
    partial: z.number().optional(),
    void: z.number(),
    waiting_for_confirmations: z.number().optional(),
  }),
  gateways: z.array(z.string()),
});

const invoicePaymentSessionStateSchema = z.object({
  invoice_payable: z.boolean(),
  resumable: z.boolean(),
  resumable_gateway: z.string().nullable(),
  gateway: z.string().nullable().optional(),
  crypto_gateway: z.string().nullable().optional(),
  processor: z.string().nullable().optional(),
  flow_type: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  provider_reference: z.string().nullable().optional(),
  provider_reference_type: z.string().nullable().optional(),
  capture_id: z.string().nullable().optional(),
  manual_confirmation_pending: z.boolean().optional(),
  payment_method_switch_locked: z.boolean().optional(),
  awaiting_payment_method: z.boolean().optional(),
  started_at: z.number().nullable().optional(),
  completed_at: z.number().nullable().optional(),
});

const embedInvoicePaymentSessionStateSchema = invoicePaymentSessionStateSchema.extend({
  gateway: z.string().nullable(),
  flow_type: z.string().nullable(),
  status: z.string().nullable(),
  manual_confirmation_pending: z.boolean(),
  payment_method_switch_locked: z.boolean(),
});

// Historic-data tolerance: invoices created before the embed surface collapsed
// to a single design froze an `embed_design` key into
// `invoices.checkout_style_snapshot`, and the API still serves those rows. This
// object is non-strict, so the retired key is accepted and stripped rather than
// rejected. Nothing reads it.
//
// The tolerance is deliberate but NOT self-evident — a non-strict object looks
// like an oversight next to the `.strict()` schemas around it — so the
// behaviour is pinned by
// `apps/backend/tests/unit/style-center/invoice-wire-embed-design-tolerance.test.ts`
// and listed for deletion in `docs/architecture/domains/style-center.md`,
// Release 2. Unlike the transport and session shims this one has no runtime
// signal to watch: the retired key is frozen into already-written invoice rows
// rather than arriving from a client, so its removal condition is a data
// question (are there pre-release invoices still being served?), answered
// against `invoices.checkout_style_snapshot`, not a log line.
const checkoutStyleStateSchema = z.object({
  theme_id: z.string().nullable(),
  surface: z.enum(['checkout', 'payment_link', 'embed']),
  token_schema_version: z.literal(1),
  revision: z.number(),
  tokens: unknownRecordSchema,
  custom_css: z.string(),
  css_variables: z.record(z.string(), z.string()),
});

const paymentRescueSchema = z.object({
  link_id: z.string(),
  source_gateway: z.string().nullable(),
  invoice_uniqid: z.string(),
  deprioritize_source_gateway: z.boolean().optional(),
});

const discordIntegrationStateSchema = z.object({
  enabled: z.boolean(),
  required: z.boolean(),
  connected: z.boolean(),
});

const shopPaymentGatewayFeeSchema = z.object({
  gateway: z.string().nullable().optional(),
  active_type: z.string().nullable().optional(),
  percent_amount: stringOrNumberSchema.nullable().optional(),
  fixed_amount: stringOrNumberSchema.nullable().optional(),
  fixed_currency: z.string().nullable().optional(),
});

const invoiceRewardItemSchema = z.object({
  id: z.string(),
  type: z.enum(['WALLET_CREDIT', 'COUPON']),
  status: z.enum(['PENDING', 'FULFILLED', 'FAILED', 'REVOKED']),
  amount: z.string().nullable(),
  currency: z.string().nullable(),
  coupon_code: z.string().nullable(),
  reason: z.enum([
    'ORDER_COMPLETED',
    'ORDER_COUNT_REACHED',
    'SPEND_AMOUNT_REACHED',
    'POSITIVE_REVIEW_LEFT',
    'FIRST_PURCHASE_COMPLETED',
  ]),
  trigger_reference_type: z.string(),
  trigger_reference_id: z.string(),
  created_at: z.number(),
  fulfilled_at: z.number().nullable(),
  expires_at: z.number().nullable(),
});

const invoiceRewardsSchema = z.object({
  summary: z.object({
    available: z.string(),
    pending: z.string(),
    lifetime_earned: z.string(),
    redeemed: z.string().nullable(),
    currency: z.string(),
  }),
  activity: z.array(invoiceRewardItemSchema),
  earned_after_invoice: z.array(invoiceRewardItemSchema),
  pending_after_invoice: z.array(invoiceRewardItemSchema),
});

/**
 * Public invoice JSON after `toInvoiceTransportSnakeCase`.
 *
 * Gateway-specific invoice row fields can still pass the backend allowlist.
 * Unknown top-level keys are therefore preserved, while every known key is
 * validated whenever it is present.
 */
export const publicInvoiceWireSchema = z.object({
  already_paid_amount: stringOrNumberSchema.nullable().optional(),
  apm_method: z.string().nullable().optional(),
  blockchain: z.string().nullable(),
  buyer_identity: buyerIdentitySchema.nullable().optional(),
  checkout_tipping: checkoutTippingSchema.nullable().optional(),
  country_regulations: z.string().nullable().optional(),
  /**
   * THAT a coupon is on the order — never WHICH one.
   *
   * The redeemed code has no key on this contract, and none is coming back to
   * it: this is the wire of the anonymous invoice read, whose only credential
   * is knowledge of the invoice URL. Coupon codes are merchant-authored and
   * routinely private (campaign, influencer, single-buyer codes), so naming one
   * to an unproven reader discloses the merchant's code. The producer withholds
   * it too — `coupon_code`, `coupon_id` and the raw `discount_breakdown` are all
   * outside `PUBLIC_INVOICE_FIELDS` — so the summary prints "Coupon −$9.80".
   *
   * A buyer-bound lane may name the coupon, but only from a source that proves
   * the reader: this schema is not it.
   */
  coupon_applied: z.boolean().optional(),
  /**
   * How much of the order the applied coupon reaches, in lines — null when it
   * covers every line or no coupon is on the order. Counts only, so nothing
   * about which products the merchant scoped the code to leaves the server.
   * The discount line already shows the reduced amount; this is what lets the
   * checkout say "applied to 1 of 3 items" instead of leaving a smaller-than-
   * advertised discount to read as a broken code.
   */
  coupon_scope: z.object({
    eligible_line_count: z.number(),
    line_count: z.number(),
  }).nullable().optional(),
  crypto_mode: z.string().nullable().optional(),
  crypto_transactions: z.array(cryptoTransactionSchema).optional(),
  currency: z.string().nullable(),
  custom_fields: unknownRecordSchema.nullable().optional(),
  custom_gateways: z.array(customGatewaySchema).optional(),
  external_payment_adapters: z.array(externalPaymentAdapterSchema).optional(),
  customer_balance: customerBalanceSchema.nullable().optional(),
  customer_email_masked: z.string().nullable().optional(),
  dark_mode: z.union([z.literal(0), z.literal(1)]).optional(),
  delivery: deliverySchema.optional(),
  delivery_info: deliverySchema.optional(),
  discount: z.number().optional(),
  discount_display: stringOrNumberSchema.optional(),
  eligible_gateways: z.array(z.string()).optional(),
  environment: z.string().optional(),
  fee_breakdown: unknownRecordSchema.nullable().optional(),
  gateway: z.string().nullable(),
  gateway_data: unknownRecordSchema.nullable().optional(),
  gateway_eligibility: z.record(z.string(), gatewayEligibilityStateSchema).optional(),
  gateway_fee_previews: z.array(gatewayFeePreviewSchema).optional(),
  gateways_available: z.array(z.string()),
  license: z.union([unknownRecordSchema, z.literal(false)]).optional(),
  name: z.string().nullable(),
  paddle_token: z.string().nullable().optional(),
  paddle_transaction_id: z.unknown().optional(),
  payment_link_id: z.string().nullable().optional(),
  payment_method_overrides: z.array(paymentMethodOverrideSchema).optional(),
  pending_custom_fields: pendingPostPaymentCustomFieldsSchema.nullable().optional(),
  payment_session_state: invoicePaymentSessionStateSchema.nullable().optional(),
  polling: pollingSchema,
  pricing_breakdown: pricingBreakdownSchema.nullable().optional(),
  product: z.array(
    publicInvoiceWireProductSchema.superRefine(requirePriceDisplayUnlessPayWhatYouWant),
  ).optional(),
  quantity: z.number().nullable(),
  rates_snapshot: z.record(z.string(), z.number()).optional(),
  remaining_amount: stringOrNumberSchema.nullable().optional(),
  selected_gateway: z.string().nullable().optional(),
  shop_checkout_ambient_color: z.string().nullable().optional(),
  shop_cloudflare_image_id: z.string().nullable(),
  shop_domain: z.string().nullable().optional(),
  shop_force_paypal_email_delivery: z.union([z.literal(0), z.literal(1)]).optional(),
  shop_image_name: z.string().nullable(),
  shop_image_storage: z.string().nullable().optional(),
  shop_payment_gateways_fees: z.array(unknownRecordSchema).optional(),
  shop_paypal_credit_card: z.union([z.literal(0), z.literal(1)]).optional(),
  shop_return_url: z.string().nullable().optional(),
  shop_slug: z.string().nullable().optional(),
  shop_terms_enabled: z.boolean().optional(),
  shop_terms_of_service: z.string().nullable().optional(),
  shop_terms_url: z.string().nullable().optional(),
  shop_privacy_policy_url: z.string().nullable().optional(),
  shop_refund_policy_url: z.string().nullable().optional(),
  shop_walletconnect_id: z.string().nullable().optional(),
  status: z.string().nullable(),
  status_history: z.array(statusHistoryEntrySchema).optional(),
  status_history_legacy: z.array(statusHistoryEntrySchema).optional(),
  status_history_raw: z.array(statusHistoryEntrySchema).optional(),
  stripe_publishable_key: z.string().nullable().optional(),
  stripe_user_id: z.string().nullable().optional(),
  subtotal: z.number().optional(),
  subtype: z.string().nullable().optional(),
  support: supportSchema.optional(),
  theme: z.string().optional(),
  tip_amount: stringOrNumberSchema.nullable().optional(),
  tip_amount_display: stringOrNumberSchema.nullable().optional(),
  tip_metadata: z.unknown().optional(),
  total: stringOrNumberSchema,
  total_conversions: unknownRecordSchema.optional(),
  total_display: stringOrNumberSchema,
  type: z.string().nullable(),
  underpayment: underpaymentSchema.nullable().optional(),
  underpayment_status: z.string().nullable().optional(),
  uniqid: z.string(),
  void_times: z.array(voidTimeSchema).optional(),

  // PUBLIC_INVOICE_FIELDS entries not exercised by the golden fixtures.
  addons: z.unknown().optional(),
  affiliate_data: z.unknown().optional(),
  affiliate_revenue_customer_id: z.string().nullable().optional(),
  bill_info: z.unknown().optional(),
  binance_checkout_url: z.string().nullable().optional(),
  binance_invoice_id: z.string().nullable().optional(),
  binance_qrcode: z.string().nullable().optional(),
  bundle_config: z.unknown().optional(),
  bundles: z.unknown().optional(),
  cashapp_cashtag: z.string().nullable().optional(),
  cashapp_note: z.string().nullable().optional(),
  cashapp_qrcode: z.string().nullable().optional(),
  created_at: z.string().optional(),
  crypto_address: z.string().nullable().optional(),
  // null when the invoice has no crypto leg (card, PayPal, unselected gateway).
  crypto_amount: stringOrNumberSchema.nullable().optional(),
  crypto_confirmations_needed: z.number().nullable().optional(),
  crypto_exchange_rate: stringOrNumberSchema.nullable().optional(),
  crypto_received: stringOrNumberSchema.nullable().optional(),
  crypto_uri: z.string().nullable().optional(),
  developer_invoice: z.unknown().optional(),
  developer_return_url: z.string().nullable().optional(),
  developer_title: z.string().nullable().optional(),
  exchange_rate: stringOrNumberSchema.nullable().optional(),
  external_order_id: z.string().nullable().optional(),
  lex_order_id: z.string().nullable().optional(),
  lex_payment_method: z.string().nullable().optional(),
  paydash_payment_id: z.string().nullable().optional(),
  paypal_apm: z.string().nullable().optional(),
  paypal_email_delivery: z.boolean().optional(),
  paypal_fee: stringOrNumberSchema.nullable().optional(),
  paypal_order_id: z.string().nullable().optional(),
  paypal_payer_email: z.string().nullable().optional(),
  paypal_subscription_id: z.string().nullable().optional(),
  paypal_subscription_link: z.string().nullable().optional(),
  perfectmoney_id: z.string().nullable().optional(),
  product_addons: z.unknown().optional(),
  product_id: z.string().nullable().optional(),
  product_title: z.string().nullable().optional(),
  product_type: z.string().nullable().optional(),
  product_variants: z.unknown().optional(),
  recurring_billing_id: z.string().nullable().optional(),
  skrill_link: z.string().nullable().optional(),
  skrill_sid: z.string().nullable().optional(),
  status_details: z.unknown().optional(),
  stripe_apm: z.string().nullable().optional(),
  stripe_client_secret: z.string().nullable().optional(),
  stripe_id: z.string().nullable().optional(),
  stripe_price_id: z.string().nullable().optional(),
  subscription: z.unknown().optional(),
  subscription_id: z.string().nullable().optional(),
  sumup_id: z.string().nullable().optional(),
  telegram_stars_payment_link: z.string().nullable().optional(),
  telegram_stars_payment_note: z.string().nullable().optional(),
  updated_at: z.string().optional(),
  virtual_payments_id: z.string().nullable().optional(),
  void_details: z.unknown().optional(),
  // EU right-of-withdrawal consent (§ 356 Abs. 5 BGB / CRD Art. 16(m)).
  // `withdrawal_consent_required` is the server's verdict for THIS invoice
  // (shop opt-in + buyer country + digital deliverables, renewals exempt) and is
  // REQUIRED on the wire: a missing verdict is a producer bug, not a licence for
  // the checkout to assume `false` and show a pay button the backend refuses.
  // The other two are the stored proof — always present, null until the buyer
  // confirms. The checkout gates on these three and never infers the
  // requirement itself.
  withdrawal_consent_at: z.string().nullable(),
  withdrawal_consent_required: z.boolean(),
  withdrawal_consent_text_version: z.string().nullable(),
}).catchall(z.unknown());

export type PublicInvoiceWire = z.infer<typeof publicInvoiceWireSchema>;

export const embedCheckoutProductWireSchema = publicInvoiceWireProductSchema.extend({
  available_addons: z.array(publicInvoiceWireAvailableAddonSchema).optional(),
  delivery_instructions: z.string().nullable().optional(),
  delivery_instructions_config: z.object({
    enabled: z.literal(true),
    required: z.boolean(),
    label: z.string(),
    placeholder: z.string().nullable().optional(),
    help_text: z.string().nullable().optional(),
    max_length: z.number(),
  }).nullable().optional(),
  delivery_instructions_label: z.string().nullable().optional(),
  image_url: z.string().nullable().optional(),
  redirectLink: z.string().nullable().optional(),
  redirectTime: z.number().nullable().optional(),
  serviceText: z.string().nullable().optional(),
}).superRefine(requirePriceDisplayUnlessPayWhatYouWant);

export type EmbedCheckoutProductWire = z.infer<typeof embedCheckoutProductWireSchema>;

/**
 * Invoice carried by the embed-start response.
 *
 * The public invoice contract validates the shared wire. These additions pin
 * fields that the checkout consumes directly and the legacy `products` alias
 * accepted by embedded clients.
 */
export const embedCheckoutInvoiceWireSchema = publicInvoiceWireSchema.extend({
  uniqid: z.string().uuid(),
  shop_id: z.union([z.string(), z.number()]).nullable(),
  customer_email: z.string().nullable(),
  checkout_style: checkoutStyleStateSchema.nullable().optional(),
  coupon_id: z.string().nullable().optional(),
  // Merchant toggle (shop_commerce_settings.coupon_field_always_visible):
  // render the coupon input expanded instead of behind the "Add coupon code"
  // trigger. Presentation only — coupon validation is unchanged.
  //
  // Required, not optional: the public producer (`InvoiceEnricher`) always
  // resolves this to a boolean, so an absent field means the producer broke,
  // not that the merchant left it unset. Failing here beats the checkout
  // quietly rendering the collapsed default for a shop that turned it on.
  coupon_field_always_visible: z.boolean(),
  discount_breakdown: unknownRecordSchema.nullable().optional(),
  affiliate_code: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  paypal_email: z.string().nullable().optional(),
  pandabase_available_payment_methods: z.array(z.string()).optional(),
  payment_rescue: paymentRescueSchema.nullable().optional(),
  discord_integration: discordIntegrationStateSchema.nullable().optional(),
  payment_session_state: embedInvoicePaymentSessionStateSchema.nullable().optional(),
  shop_payment_gateways_fees: z.array(shopPaymentGatewayFeeSchema).optional(),
  product: z.array(embedCheckoutProductWireSchema).optional(),
  products: z.array(embedCheckoutProductWireSchema).optional(),
  rewards: invoiceRewardsSchema.nullable().optional(),
}).superRefine((invoice, context) => {
  if (invoice.product === undefined && invoice.products === undefined) {
    context.addIssue({
      code: 'custom',
      message: 'Embed checkout invoice must include product or products.',
      path: ['product'],
    });
  }
});

export type EmbedCheckoutInvoiceWire = z.infer<typeof embedCheckoutInvoiceWireSchema>;

/** Critical runtime boundary for the public embed-start response. */
export const embedCheckoutStartWireSchema = z.object({
  data: z.object({
    invoice: embedCheckoutInvoiceWireSchema,
    invoice_url: z.string().nullable(),
    checkout_url: z.string().nullable(),
    selected_gateway: z.string().nullable(),
    session_started: z.boolean(),
    payment_session: embedPaymentSessionWireSchema.nullable(),
    completion_access_grant: z.string().nullable(),
  }).passthrough(),
}).passthrough();

export type EmbedCheckoutStartWire = z.infer<typeof embedCheckoutStartWireSchema>;
